import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowDownRight,
  Gift,
  Undo2,
  Send,
  Ticket,
  BadgeCheck,
  Copy,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CompactStatCard, FormInput, PageHeader, StatsGrid } from "@/components/shared";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  useMemberProfile,
  useMyWallet,
  useP2pTransferMutation,
  useReferralSummary,
  useTransferPreviewMutation,
  useUserCoupons,
  useWalletTransactions,
  useWithdrawMutation,
  useAdminCoupons,
} from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { Skeleton } from "@/components/ui/skeleton";
import { UserCouponStatus } from "@/types/customer.dto";
import type { MemberTransferPreviewDto } from "@/types/wallet.dto";

const AccountWallet = () => {
  const { toast } = useToast();
  const { data: wallet, isLoading: walletLoading } = useMyWallet();
  const { data: memberProfile } = useMemberProfile();
  const { data: coupons, isLoading: couponsLoading } = useUserCoupons();
  const { data: platformCouponsData, isLoading: platformCouponsLoading } = useAdminCoupons({ isActive: true });
  const platformCoupons = platformCouponsData?.items ?? [];
  const { data: referralSummary } = useReferralSummary();
  const { data: transactionsData, isLoading: txLoading } = useWalletTransactions(
    wallet?.id ?? 0,
    { pageSize: 50 },
    Boolean(wallet?.id),
  );
  const withdrawMutation = useWithdrawMutation();
  const transferMutation = useP2pTransferMutation();
  const previewMutation = useTransferPreviewMutation();
  const fundBalance = wallet?.availableBalance ?? 0;
  const walletTransactions = transactionsData?.items ?? [];
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferStep, setTransferStep] = useState<"form" | "confirm">("form");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [transferForm, setTransferForm] = useState({ memberCode: "", amount: "", note: "" });
  const [recipient, setRecipient] = useState<MemberTransferPreviewDto | null>(null);

  const typeIcon: Record<string, { icon: typeof ArrowUpRight; color: string; label: string }> = {
    order_payment: { icon: ArrowDownRight, color: "text-destructive bg-destructive/10", label: "Order Payment" },
    order_refund: { icon: Undo2, color: "text-info bg-info/10", label: "Refund" },
    referral_commission: { icon: ArrowUpRight, color: "text-success bg-success/10", label: "Referral" },
    delivery_commission: { icon: ArrowUpRight, color: "text-success bg-success/10", label: "Delivery Commission" },
    store_settlement: { icon: ArrowUpRight, color: "text-primary bg-primary/10", label: "Store Settlement" },
    admin_commission: { icon: ArrowDownRight, color: "text-muted-foreground bg-muted", label: "Admin Commission" },
    wallet_top_up: { icon: ArrowUpRight, color: "text-success bg-success/10", label: "Wallet Top-up" },
    cashback_reward: { icon: Gift, color: "text-primary bg-primary/10", label: "Cashback" },
    withdrawal: { icon: ArrowDownRight, color: "text-destructive bg-destructive/10", label: "Withdrawal" },
    p2ptransfer_in: { icon: ArrowUpRight, color: "text-success bg-success/10", label: "P2P In" },
    p2ptransfer_out: { icon: Send, color: "text-warning bg-warning/10", label: "P2P Out" },
    reward_pool_bonus: { icon: Gift, color: "text-primary bg-primary/10", label: "Reward Pool Bonus" },
    self_cashback: { icon: Gift, color: "text-success bg-success/10", label: "Self Cashback" },
    level_cashback: { icon: ArrowUpRight, color: "text-success bg-success/10", label: "Level Cashback" },
    reward_cashback: { icon: Gift, color: "text-primary bg-primary/10", label: "Reward" },
    income_reversal: { icon: Undo2, color: "text-destructive bg-destructive/10", label: "Income Reversal" },
    admin_adjustment: { icon: ArrowDownRight, color: "text-warning bg-warning/10", label: "Admin Adjustment" },
  };

  const unusedCoupons = coupons?.filter((c) => c.status === UserCouponStatus.Unused) ?? [];
  const isMemberActive = Boolean(memberProfile?.isMemberActive);

  const copyMemberId = () => {
    if (!memberProfile?.memberCode) return;
    navigator.clipboard.writeText(memberProfile.memberCode);
    toast({ title: "Member ID copied" });
  };

  const resetTransfer = () => {
    setTransferForm({ memberCode: "", amount: "", note: "" });
    setRecipient(null);
    setTransferStep("form");
  };

  const handleTransferOpenChange = (open: boolean) => {
    setTransferOpen(open);
    if (!open) resetTransfer();
  };

  const handleContinueTransfer = async () => {
    const amount = Number(transferForm.amount);
    if (!transferForm.memberCode.trim()) {
      toast({ title: "Enter recipient Member ID", variant: "destructive" });
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: "Enter a valid amount", variant: "destructive" });
      return;
    }
    if (amount > fundBalance) {
      toast({ title: "Insufficient balance", variant: "destructive" });
      return;
    }

    try {
      const preview = await previewMutation.mutateAsync(transferForm.memberCode.trim());
      setRecipient(preview);
      setTransferStep("confirm");
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleConfirmTransfer = async () => {
    try {
      await transferMutation.mutateAsync({
        toMemberCode: transferForm.memberCode,
        amount: Number(transferForm.amount),
        note: transferForm.note || undefined,
      });
      toast({
        title: "Transfer completed",
        description: `₹${Number(transferForm.amount).toFixed(0)} sent to ${transferForm.memberCode}`,
      });
      handleTransferOpenChange(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wallet & Funds"
        description="Fund balance, member coupons, P2P transfers, and transaction history."
      />

      {isMemberActive && memberProfile?.memberCode ? (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <BadgeCheck className="h-8 w-8 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Your Member ID</p>
                <p className="font-heading font-bold text-lg">{memberProfile.memberCode}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={copyMemberId}>
              <Copy className="h-4 w-4" /> Copy
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 text-warning" />
              <div>
                <p className="text-sm font-semibold">Activate Member ID to use P2P</p>
                <p className="text-xs text-muted-foreground">
                  Wallet-to-wallet transfers require an active Member ID.
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" asChild>
              <Link to="/account/member-id">Activate Member ID</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="bg-gradient-primary text-primary-foreground">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-primary-foreground/70 text-sm">Fund Balance</p>
              <p className="text-4xl font-heading font-bold mt-1">
                {walletLoading ? "…" : `₹${fundBalance.toFixed(0)}`}
              </p>
              {referralSummary?.pendingEarnings != null && referralSummary.pendingEarnings > 0 && (
                <p className="text-xs text-primary-foreground/70 mt-2">
                  ₹{referralSummary.pendingEarnings.toFixed(0)} pending referral earnings
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={() => setWithdrawOpen(true)}>Withdraw</Button>
              <Button
                variant="secondary"
                size="sm"
                className="gap-1"
                disabled={!isMemberActive}
                onClick={() => setTransferOpen(true)}
              >
                <Send className="h-4 w-4" /> P2P Transfer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <StatsGrid columns={3}>
        <CompactStatCard title="Referral Earnings" value={`₹${referralSummary?.totalEarnings?.toFixed(0) ?? 0}`} icon={ArrowUpRight} />
        <CompactStatCard title="Active Referrals" value={referralSummary?.activeReferrals ?? 0} icon={Gift} />
        <CompactStatCard title="Unused Coupons" value={memberProfile?.unusedCouponCount ?? unusedCoupons.length} icon={Ticket} />
      </StatsGrid>

      <Tabs defaultValue="transactions">
        <TabsList>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="coupons">My Coupons ({unusedCoupons.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg">Recent Transactions</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link to="/account/wallet/transactions">View all</Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {txLoading ? (
                Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)
              ) : walletTransactions.length > 0 ? (
                walletTransactions.map((txn) => {
                  const meta = typeIcon[txn.type] ?? typeIcon.referral_commission;
                  const Icon = meta.icon;
                  return (
                    <div key={txn.id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                      <div className={cn("flex h-10 w-10 items-center justify-center rounded-full", meta.color)}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{meta.label}</p>
                        <p className="truncate text-xs text-muted-foreground">{txn.description}</p>
                        <p className="text-[11px] text-muted-foreground">{txn.date}</p>
                      </div>
                      <p className={cn("text-sm font-semibold", txn.isCredit ? "text-success" : "text-destructive")}>
                        {txn.isCredit ? "+" : "-"}₹{Math.abs(txn.amount).toFixed(0)}
                      </p>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">No transactions yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coupons" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: My Wallet Coupons */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-primary" />
                  My Wallet Coupons
                </CardTitle>
                <p className="text-xs text-muted-foreground">Coupons issued to your wallet on Member ID activation or milestone rewards.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {couponsLoading ? (
                  Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)
                ) : unusedCoupons.length > 0 || (coupons?.length ?? 0) > 0 ? (
                  (coupons ?? []).map((coupon) => (
                    <div key={coupon.id} className="flex items-center justify-between rounded-xl border border-border/80 p-3 bg-card hover:shadow-xs transition-all">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-bold font-mono">{coupon.code}</p>
                          {coupon.status === UserCouponStatus.Unused && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-primary shrink-0"
                              onClick={() => {
                                navigator.clipboard.writeText(coupon.code);
                                toast({ title: "Coupon code copied!" });
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">₹{coupon.discountValue} discount</p>
                      </div>
                      <Badge variant={coupon.status === UserCouponStatus.Unused ? "default" : "secondary"}>
                        {coupon.statusText}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No wallet coupons yet. Activate your Member ID from Profile to receive coupons.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Right Column: Platform & Store Offers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Gift className="w-5 h-5 text-primary" />
                  Platform & Store Offers
                </CardTitle>
                <p className="text-xs text-muted-foreground">Active discount coupons created by store owners or administrators.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {platformCouponsLoading ? (
                  Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)
                ) : platformCoupons.length > 0 ? (
                  platformCoupons.map((coupon) => (
                    <div key={coupon.id} className="flex items-start justify-between rounded-xl border border-border/80 p-3 bg-card hover:shadow-xs transition-all gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold font-mono text-primary bg-primary/5 px-2 py-0.5 rounded-lg border border-primary/10">{coupon.code}</p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-primary shrink-0"
                            onClick={() => {
                              navigator.clipboard.writeText(coupon.code);
                              toast({ title: "Promo code copied!" });
                            }}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                          {coupon.minOrderAmount > 0 && (
                            <span className="text-[10px] text-muted-foreground font-medium">Min order ₹{coupon.minOrderAmount}</span>
                          )}
                        </div>
                        <p className="text-xs font-semibold text-foreground mt-1.5">{coupon.name}</p>
                        {coupon.description && (
                          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{coupon.description}</p>
                        )}
                      </div>
                      <span className="font-bold text-sm text-emerald-600 dark:text-emerald-400 shrink-0">
                        {coupon.type === 0 ? `${coupon.discountValue}% Off` : `₹${coupon.discountValue} Off`}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No active store or platform offers at the moment.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fund Withdrawal</DialogTitle></DialogHeader>
          <FormInput
            label="Amount (₹)"
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawOpen(false)}>Cancel</Button>
            <Button disabled={!wallet?.id || !withdrawAmount} onClick={async () => {
              if (!wallet?.id) return;
              try {
                await withdrawMutation.mutateAsync({
                  walletId: wallet.id,
                  dto: { amount: Number(withdrawAmount), paymentMode: "Bank Transfer" },
                });
                toast({ title: "Withdrawal request submitted" });
                setWithdrawOpen(false);
                setWithdrawAmount("");
              } catch (error) {
                toast({ title: getApiErrorMessage(error), variant: "destructive" });
              }
            }}>Withdraw</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={handleTransferOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              {transferStep === "form" ? "P2P Transfer" : "Confirm Transfer"}
            </DialogTitle>
            <DialogDescription>
              {transferStep === "form"
                ? "Send wallet balance to another active member by Member ID."
                : "Review details before sending funds."}
            </DialogDescription>
          </DialogHeader>

          {transferStep === "form" ? (
            <div className="space-y-4">
              <FormInput
                label="Recipient Member ID"
                value={transferForm.memberCode}
                onChange={(e) => setTransferForm({ ...transferForm, memberCode: e.target.value.toUpperCase() })}
                placeholder="MBR123456"
              />
              <FormInput
                label="Amount (₹)"
                type="number"
                value={transferForm.amount}
                onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
              />
              <FormInput
                label="Note (optional)"
                value={transferForm.note}
                onChange={(e) => setTransferForm({ ...transferForm, note: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Available: ₹{fundBalance.toFixed(0)}</p>
            </div>
          ) : (
            <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">To</span>
                <span className="font-mono font-semibold">{recipient?.memberCode}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Recipient</span>
                <span className="font-medium">{recipient?.displayName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-semibold">₹{Number(transferForm.amount).toFixed(0)}</span>
              </div>
              {transferForm.note && (
                <div className="flex justify-between gap-4 text-sm">
                  <span className="text-muted-foreground">Note</span>
                  <span className="text-right">{transferForm.note}</span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {transferStep === "confirm" ? (
              <>
                <Button variant="outline" onClick={() => setTransferStep("form")}>Back</Button>
                <Button disabled={transferMutation.isPending} onClick={handleConfirmTransfer}>
                  {transferMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    "Confirm & Send"
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => handleTransferOpenChange(false)}>Cancel</Button>
                <Button
                  disabled={!transferForm.memberCode || !transferForm.amount || previewMutation.isPending}
                  onClick={handleContinueTransfer}
                >
                  {previewMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking…</>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountWallet;
